import React, { useContext } from 'react';

import { Select } from '@aline/common';
import { Box, Chip, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import type { UrlRelationshipRow } from 'src/components';
import { AlineTooltip, RelationshipTypeSelect } from 'src/components';
import { getRelationshipLabel } from 'src/components/generic/SelectRelatedDocumentsWithRelationshipDialog';
import { IconColors } from 'src/components/icons/icon-utils';
import { UrlLinkIcon } from 'src/components/icons/UrlLinkIcon';
import type { Apollo } from 'src/gql/generated';
import { getExternalUrl, openLink } from 'src/helpers/url';
import { useRelationshipActions } from 'src/hooks';

import { RelationshipActionButtons } from './RelationshipActionButtons';
import { DocumentGridReportIdContext } from './RepositoryContextProviders';
import { useRelationship } from './repositoryDocumentNameCellHelpers';

interface UrlRelationshipRowCellProps {
  classes: Record<
    'cell' | 'directionLabel' | 'nameContainer' | 'relationshipActions' | 'relationshipLabel',
    string
  >;
  row: UrlRelationshipRow;
}

export const UrlRelationshipRowCell = React.memo(function UrlRelationshipRowCell({
  classes,
  row,
}: UrlRelationshipRowCellProps): JSX.Element {
  const { urlRelationship } = row;
  const repositoryId = useContext(DocumentGridReportIdContext);
  const actions = useRelationshipActions({
    repositoryId,
    row,
  });
  const childUrl = urlRelationship.childUrl ?? '';
  const { relationship } = useRelationship(row);

  return (
    <div className={classes.cell}>
      <div className={classes.nameContainer}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
          }}
        >
          <UrlLinkIcon height={16} stroke={IconColors.Primary} width={16} />
          <AlineTooltip arrow placement="top" title={childUrl}>
            <Typography
              noWrap
              component="a"
              href={getExternalUrl(childUrl)}
              rel="noopener noreferrer"
              sx={{
                '&:hover': { textDecoration: 'underline' },
                color: 'primary.main',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 700,
                textDecoration: 'none',
              }}
              target="_blank"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                openLink(childUrl);
              }}
            >
              {childUrl || 'Untitled'}
            </Typography>
          </AlineTooltip>
        </Box>
        <Box
          className={classes.relationshipActions}
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 0.5,
          }}
        >
          <Chip
            className={classes.directionLabel}
            data-cy={Select.relationships.directionBadge}
            label="CHILD"
            size="small"
          />
          <RelationshipTypeSelect
            disableUnderline
            data-cy={Select.relationships.typeSelect}
            getLabel={type => getRelationshipLabel({ direction: 'child', type })}
            menuTitle="Select the type of child relation"
            searchPlaceholder="Search for a type"
            size="small"
            value={relationship?.childRelationshipType ?? ''}
            variant="standard"
            onChange={async (event: SelectChangeEvent<Apollo.ChildRelationshipType | ''>) => {
              event.stopPropagation();
              const newType = event.target.value;
              if (!newType) return;
              await actions.handleUpdateRelationshipType(newType);
            }}
          />
          <RelationshipActionButtons
            confirmButtonCy={Select.relationships.url.confirmButton}
            rejectButtonCy={Select.relationships.url.rejectButton}
            row={row}
          />
        </Box>
      </div>
    </div>
  );
});
